import AutoComplete from '@tarekraafat/autocomplete.js';
import {getPageCountryCode, getPageLanguageCode} from '@malt/dom';

export class LocationAutocomplete {
    /**
     * Initiate a new autocomplete instance
     *
     * @param {Object} options
     * @param {"LOCALITY" | "FULL_ADDRESS"} options.layer The layer to be used for the results
     * @param {boolean=} options.debug true to prevent the dropdown to close on blur
     * @param {HTMLInputElement} options.input the input on which will be bound the autocomplete
     * @param {function} options.onSelect callback called with the selected value
     * @param {string=} options.countryCode country used to display the results
     * @param {Object=} options.defaultValue initial value
     * @param {array=} options.defaultCitySuggestions suggestions displayed when the input is focused and empty
     * @param {Object=} options.focusPoint coordinates used to bias the search to get the nearest results
     * @param {number} options.focusPoint.lon longitude of the point to focus on
     * @param {number} options.focusPoint.lat latitude of the point to focus on
     * @param {string=} options.languageCode language used to display the results
     * @param {number=} options.maxResults maximum amount of results to display
     * @param {boolean=} options.remoteIfEmpty true if the selected value and input should rest to input if no other value is selected
     * @param {string=} options.remoteCountryOptionLabel the label of the remote option, it will not be displayed if absent
     * @param {string=} options.remoteEuropaLabel the label of the europa remote option, it will not be displayed if absent
     */
    constructor({
        input,
        onSelect,
        countryCode = getPageCountryCode(),
        debug = false,
        defaultValue = null,
        defaultCitySuggestions = [],
        focusPoint = null,
        layer = 'FULL_ADDRESS',
        languageCode = getPageLanguageCode(),
        maxResults = 5,
        remoteIfEmpty = false,
        remoteCountryOptionLabel = null,
        remoteEuropaLabel = null,
        autocompleteUrl = '/api/location/autocomplete',
    }) {
        this.countryCode = countryCode;
        this.debug = debug;
        this.defaultCitySuggestions = defaultCitySuggestions;
        this.input = input;
        this.languageCode = languageCode;
        this.onSelect = onSelect;
        this.lastQueryTimestamp = new Date();
        this.lastSelected = null;
        this.remoteEuropa = remoteEuropaLabel
            ? {
                  remoteEuropa: true,
                  formattedAddress: remoteEuropaLabel,
              }
            : null;

        this.remoteCountrySuggestion = remoteCountryOptionLabel
            ? {
                  remoteAllowed: true,
                  formattedAddress: remoteCountryOptionLabel,
              }
            : null;
        this.maxResults = maxResults;
        this.layer = layer;
        this.remoteIfEmpty = remoteIfEmpty;
        this.autocompleteUrl = autocompleteUrl;
        this.focusPoint = focusPoint;

        this._createAutoComplete = this._createAutoComplete.bind(this);
        this._fetchSuggestions = this._fetchSuggestions.bind(this);
        this._onBlur = this._onBlur.bind(this);
        this._onSuggestionSelected = this._onSuggestionSelected.bind(this);
        this.setSelectedValue = this.setSelectedValue.bind(this);

        this.setSelectedValue(defaultValue);

        this._createAutoComplete();
    }

    get defaultSuggestions() {
        const remoteSuggestions = [this.remoteCountrySuggestion, this.remoteEuropa].filter((x) => x);
        return [
            ...remoteSuggestions,
            ...this.defaultCitySuggestions.map((suggestion) => ({
                ...suggestion,
                suggestion: true,
            })),
        ];
    }

    get displayDefaultSuggestions() {
        return this.defaultSuggestions.length > 0;
    }

    setSelectedValue(value) {
        if (value && !value.remoteAllowed) {
            this.input.value = value.formattedAddress || '';
            this.lastSelected = value;
        } else if ((value && value.remoteAllowed) || this.remoteIfEmpty) {
            this.input.value = this.remoteCountrySuggestion.formattedAddress;
            this.lastSelected = this.remoteCountrySuggestion;
        } else {
            this.input.value = '';
            this.lastSelected = null;
        }
    }

    destroy() {
        if (this.autocomplete) {
            this.autocomplete.unInit();
        }
    }

    async _createAutoComplete() {
        let actualInput;
        if (this.input.nodeName === 'JOY-INPUT') {
            await customElements.whenDefined('joy-input');
            await this.input.componentOnReady();
            actualInput = this.input.querySelector('input');
        } else {
            actualInput = this.input;
        }

        // See https://tarekraafat.github.io/autoComplete.js/#/configuration
        const config = {
            data: {
                src: async (query) => {
                    if (query) {
                        return await this._fetchSuggestions(query);
                    }
                    return this.defaultSuggestions;
                },
                keys: ['formattedAddress'],
            },
            debounce: 100,
            events: {
                input: {
                    blur: this._onBlur,
                    selection: (event) => {
                        const selection = event.detail.selection.value;
                        this._onSuggestionSelected(selection);
                    },
                    focus: () => {
                        if (!this.displayDefaultSuggestions) {
                            return;
                        }
                        if (!this.lastSelected || this.lastSelected.remoteAllowed) {
                            // this triggers the display of the default suggestions
                            this.input.value = '';
                            this.autocomplete.start('');
                        }
                    },
                },
            },
            resultItem: {
                class: 'location-autocomplete__results-list__item',
                element: (item, data) => {
                    if (data.value.remoteAllowed) {
                        const icon = document.createElement('joy-icon');
                        icon.setAttribute('name', 'home');
                        item.prepend(icon);
                    }
                    if (data.value.suggestion) {
                        item.setAttribute('data-heap', 'location_suggestion');
                    }
                },
            },
            resultsList: {
                class: 'location-autocomplete__results-list',
                maxResults: this.maxResults,
            },
            // we consider all the results returned by the API, no local matching
            searchEngine: (query, record) => record,
            selector: () => actualInput,
            trigger: (query) => {
                if (this.displayDefaultSuggestions && query === '') {
                    // special case for an empty input, we want to display the suggestions
                    return true;
                }
                return query && query.trim().length > 1;
            },
            wrapper: false,
        };

        this.autocomplete = new AutoComplete(config);
        this.autocomplete.init();
    }

    async _fetchSuggestions(query) {
        const payload = {
            query,
            layer: this.layer,
            languageTag: `${this.languageCode}${this.countryCode ? `-${this.countryCode}` : ''}`,
            size: this.maxResults,
            focusPoint: this.focusPoint,
        };

        const timestamp = new Date();

        const response = await fetch(this.autocompleteUrl, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`${response.status} ${response.statusText}`);
        }

        const json = await response.json();

        if (timestamp.getTime() < this.lastQueryTimestamp.getTime()) {
            return this.lastResponse || json;
        }

        this.lastQueryTimestamp = timestamp;
        this.lastResponse = json;

        return json;
    }

    _onSuggestionSelected(selection) {
        this.setSelectedValue(selection);
        this.onSelect(this.lastSelected);
    }

    _onBlur(event) {
        // to ensure we always submit structured addresses we never want the input to keep its value
        // if the user didn't select it explicitly in the suggestions
        if (event.target.value.trim() === '' && this.lastSelected) {
            // the user cleared the field, let's consider they want to reset the address to blank
            this._onSuggestionSelected(null);
        } else if (event.target.value === ((this.lastSelected && this.lastSelected.formattedAddress) || '')) {
            // no change compared to the last selected value
        } else {
            // looks like the user let the field in an unknown state, let's reset it to its last state
            this.setSelectedValue(this.lastSelected);
        }
        if (!this.debug) {
            this.autocomplete.close();
        }
    }
}
